import { HttpStatusCode } from '@commutifi-fe/constants'
import { PasswordResetBody, usePasswordReset } from 'api/modules/authentication'
import cx from 'classnames'
import { screenSizes } from 'constants/global'
import { useIntl } from 'locales/index'
import React, { ReactNode } from 'react'
import { useMediaQuery } from 'react-responsive'
import { Link } from 'react-router-dom'
import { RegisterForm } from 'shared/components/common/RegisterForm'
import { Alert, Button, Form, Input } from '@commutifi-fe/ui'
import { useCommutifiErrorMessages } from '@commutifi-fe/commutifi-specific/hooks'
import { getCommutifiErrorCode } from 'utils/index'
import { AuthLayout } from '../layout/AuthLayout'

const commonAlertProps = {
  style: { borderRadius: '4px' },
  className: 'u-margin-bottom--large'
}

export function ResetPassword({ token }: { token: string }) {
  const intl = useIntl()
  const { error, isPending: isLoading, isSuccess, mutate: resetPassword } = usePasswordReset()
  const { translateCommutifiError } = useCommutifiErrorMessages()
  const isXSmall = useMediaQuery({ maxWidth: screenSizes.xsMaxWidth })

  const handleSubmit = (values: PasswordResetBody) => resetPassword(values)

  const renderAlert = () => {
    if (!error && !isSuccess) return null

    let message: null | string | ReactNode = null

    if (error) {
      message =
        error?.status === HttpStatusCode.NotFound
          ? intl.formatMessage({ id: 'auth.passwordReset.errors.tokenNotFound' })
          : error?.status === HttpStatusCode.Unauthorized && !getCommutifiErrorCode(error)
            ? intl.formatMessage({ id: 'auth.passwordReset.errors.authentication' })
            : translateCommutifiError(error)
    } else if (isSuccess) {
      message = (
        <span>
          {intl.formatMessage({ id: 'auth.reset.success' })}{' '}
          <Link to="/auth">{intl.formatMessage({ id: 'auth.reset.success.login' })}</Link>.
        </span>
      )
    }

    return <Alert type={error ? 'error' : isSuccess ? 'success' : undefined} {...{ ...commonAlertProps, message }} />
  }

  return (
    <AuthLayout title={intl.formatMessage({ id: 'auth.reset.title' })} message={renderAlert()}>
      <Form layout="vertical" requiredMark={false} onFinish={handleSubmit}>
        <Form.Item hidden name="passwordResetToken" initialValue={token}>
          <Input />
        </Form.Item>

        <RegisterForm.PasswordField label={intl.formatMessage({ id: 'auth.reset.newPassword' })} />
        <div className="u-flex-justify-end u-flex-align-start u-flex-wrap">
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue, getFieldsError }) => (
              <Button
                type="primary"
                htmlType="submit"
                loading={isLoading}
                block={isXSmall}
                className="u-margin-bottom--medium-2"
                disabled={
                  !getFieldValue('password') || Boolean(getFieldsError().filter(({ errors }) => errors.length).length)
                }
              >
                {intl.formatMessage({ id: 'auth.reset.submit' })}
              </Button>
            )}
          </Form.Item>
          <Button ghost block={isXSmall} className={cx(!isXSmall && 'u-margin-left--medium-2')}>
            <Link to="/auth">{intl.formatMessage({ id: 'auth.passwordReset.back' })}</Link>
          </Button>
        </div>
      </Form>
    </AuthLayout>
  )
}
